import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/shared";
import { Plus, Tags, Pencil, Trash2 } from "lucide-react";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/categories").then(({ data }) => setCats(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); return; }
    try {
      if (editId) await api.put(`/categories/${editId}`, form);
      else await api.post("/categories", form);
      toast.success(editId ? "Categoria aggiornata" : "Categoria creata");
      setOpen(false); setForm({ name: "", description: "" }); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Categoria eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Configurazione" title="Categorie" subtitle="Definisci le categorie dei beni aziendali">
        <Button onClick={() => { setForm({ name: "", description: "" }); setEditId(null); setOpen(true); }} data-testid="new-category-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova categoria
        </Button>
      </PageHeader>

      {cats.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cats.map((c) => (
            <Card key={c.id} data-testid={`category-${c.id}`} className="p-5 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Tags className="h-5 w-5 text-primary" />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ name: c.name, description: c.description }); setEditId(c.id); setOpen(true); }} data-testid={`edit-category-${c.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-category-${c.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="font-heading font-bold text-lg mt-3">{c.name}</div>
              {c.description && <p className="text-sm text-muted-foreground mt-1">{c.description}</p>}
              <div className="label-caps text-muted-foreground mt-3">{c.asset_count} beni</div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-lg"><EmptyState icon={Tags} title="Nessuna categoria" subtitle="Crea la prima categoria per organizzare i beni." /></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Autoveicoli, Utensili, Informatica" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-category-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
